import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AttainmentBar } from 'ui-deal-management-components-lib/components/attainmentBar/AttainmentBar';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { List } from 'foundations-components/transitional/components/List';
import { ListItem } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import I18n from 'I18n';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isError, isLoading } from 'reference-resolvers-lite/Status';
import { PIPELINE } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useFetchBatchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { derivePipelineNames } from '../../utils/pipelineUtils';
import { isMissingCurrencyCode } from '../../utils/currency';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { OWNER } from '../../constants/AssigneeTypes';
import { fetchAggregatedGoalBreakdown } from '../../actions/AggregatedOverviewDataActions';
import { getGoalsBreakdown, getIsFetchingGoalsBreakdown } from '../../selectors/aggregatedOverviewDataSelectors';
import { getFilters } from '../../selectors/filtersSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DealSidebarGoalsSection = ({
  assigneeType,
  isTeam,
  pipelines,
  id
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(getFilters);
  useEffect(() => {
    // Fetch aggregated goal breakdown on mount or when filters change.
    // getFilters() returns a memoized object, so we can safely use it as a dependency.
    dispatch(fetchAggregatedGoalBreakdown(isTeam, id)).catch(error => logRavenException(error, 'Fetch aggregated goal breakdown', {
      isTeam,
      id
    }));
  }, [dispatch, isTeam, id, pipelines, filters]);
  const resolver = createStandardCachedResolver(PIPELINE, '0-3');
  const {
    status,
    references,
    error
  } = useFetchBatchResolver(resolver, [...pipelines.toArray()]);
  const goalsBreakdown = useSelector(getGoalsBreakdown);
  const isFetchingGoalsBreakdown = useSelector(getIsFetchingGoalsBreakdown);
  const hasFetchedReferencesForAllPipelines = references && references.length === pipelines.size;
  if (isError(status) || error) {
    return /*#__PURE__*/_jsx(Alert, {
      marginBlockStart: "800",
      titleText: I18n.text('dealSidebar.goalsFetchingError'),
      type: "danger"
    });
  }
  if (isLoading(status) || isFetchingGoalsBreakdown || !hasFetchedReferencesForAllPipelines) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 300,
      showLabel: true,
      size: "md"
    });
  }
  const pipelineNames = derivePipelineNames(references);
  return /*#__PURE__*/_jsx(List, {
    marginBlockStart: "600",
    marginInlineStart: "200",
    children: pipelines.map(pipeline => {
      const goal = goalsBreakdown.getIn([pipeline, 'aggregatedGoalAmount']);
      const value = goalsBreakdown.getIn([pipeline, 'aggregatedClosedWonDealAmount']);
      return /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(AttainmentBar, {
          goal: goal && !isMissingCurrencyCode(goal) ? goal.value : undefined,
          value: value && !isMissingCurrencyCode(value) ? value.value : 0,
          name: pipelineNames[pipeline],
          showAvatar: false,
          renderGoal: () => {
            if (isMissingCurrencyCode(goal)) {
              return '--';
            }
            const noExplicitTeamGoal = isTeam && assigneeType === OWNER;
            if (noExplicitTeamGoal) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "dealSidebar.goalsTooltip.noTeamGoalTooltipHeader"
              });
            }
            if (!goal || !goal.value) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "dealSidebar.goalsTooltip.noGoalSetTooltipHeader"
              });
            }
            return /*#__PURE__*/_jsxs(Flex, {
              justifyContent: "space-between",
              children: [/*#__PURE__*/_jsx(View, {
                as: "span",
                marginInlineEnd: "100",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "dealSidebar.goalHelpText"
                })
              }), /*#__PURE__*/_jsx(FormattedIntlCurrency, {
                value: goal.value,
                currencyCode: goal.currencyCode
              })]
            });
          },
          renderValue: () => {
            if (isMissingCurrencyCode(value)) {
              return '--';
            }
            return /*#__PURE__*/_jsx(FormattedIntlCurrency, {
              value: value ? value.value : 0,
              currencyCode: value ? value.currencyCode : undefined
            });
          }
        })
      }, pipeline);
    })
  });
};
export default DealSidebarGoalsSection;